import React from 'react'
import {Link as ReactRouterLink} from 'react-router-dom'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Text,
} from '../../../../utils/chakraMonomorphic'
import {RouterLink as Link} from '../../../../utils/chakraRouterLink'

import {api} from '../../../../services/api'
import {
  FaceOffGoldenCase,
  FaceOffGoldenPhoto,
  FaceOffGoldenSummary,
  FaceOffGoldenVersion,
  FaceOffVerdictValue,
} from '../../../../services/coreApi/faceoff'
import {buildAlbumPhotoUrl, isLinkableId} from '../../../../utils/photoDeepLink'
import copy from './copy.json'
import {
  MAX_GOLDEN_CASES,
  MAX_GOLDEN_PHOTOS,
  VERDICT_KEYS,
  fillCopy,
} from './constants'
import {formatDelta, formatScore} from './faceOffFaces'
import {BOX_COLORS, FaceOffLightbox, FaceOffPhoto} from './FaceOffPhoto'
import {
  GoldenDetails,
  QueueCaseButton,
  QueuePhotoButton,
  RemovalQueue,
} from './FaceOffGoldenQueue'

// The slider starts at the production cut-off and keeps a 50 to 100 range;
// the number box beside it takes any value from 0 to 100.
export const DEFAULT_CUTOFF = 75
const SLIDER_MIN = 50
const SLIDER_MAX = 100

export interface ReviewItem {
  photo: FaceOffGoldenPhoto
  case: FaceOffGoldenCase
}

export const caseId = (photoId: number, personId: number): string =>
  `${photoId}:${personId}`

const settled = (v: FaceOffVerdictValue | null | undefined): boolean =>
  v === 'accept' || v === 'reject'

/**
 * Step-through order: red (no score, or under the cut-off) first, then
 * green; inside each group by photo id, then camper id, so one photo's cases
 * sit together.
 */
export const caseOrder = (items: ReviewItem[], cutoff: number): number[] => {
  const red = (i: number): number => {
    const s = items[i].case.score
    return s === null || s < cutoff ? 1 : 0
  }
  return items
    .map((_, i) => i)
    .sort(
      (a, b) =>
        red(b) - red(a) ||
        items[a].photo.photoId - items[b].photo.photoId ||
        items[a].case.personId - items[b].case.personId,
    )
}

const isTyping = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null
  if (!el) return false
  if (el.tagName === 'TEXTAREA') return true
  if (el.tagName !== 'INPUT') return false
  const {type} = el as HTMLInputElement
  return !['checkbox', 'radio', 'range'].includes(type)
}

/**
 * The case's status against the camp's own cut-off. A positive passes when the
 * run accepted the camper; a non-match passes when it did not.
 */
export const caseStatus = (
  c: FaceOffGoldenCase,
  hasRun: boolean,
): 'pass' | 'regressed' | 'unscored' => {
  if (!hasRun) return 'unscored'
  const cut = c.threshold ?? DEFAULT_CUTOFF
  const accepted = c.score !== null && c.score >= cut
  if (c.kind === 'nonmatch') return accepted ? 'regressed' : 'pass'
  if (c.score === null) return 'regressed'
  return accepted ? 'pass' : 'regressed'
}

const Key: React.FC<{children: React.ReactNode}> = ({children}) => (
  <Box
    as='kbd'
    px={1.5}
    py={0.5}
    borderWidth='1px'
    borderRadius='sm'
    bg='var(--fo-panel)'
    fontSize='xs'
    fontFamily='mono'>
    {children}
  </Box>
)

const SideArrow: React.FC<{
  label: string
  disabled: boolean
  onClick: () => void
  children: React.ReactNode
}> = ({label, disabled, onClick, children}) => (
  <Button
    alignSelf='center'
    h='64px'
    minW='40px'
    fontSize='2xl'
    variant='outline'
    aria-label={label}
    isDisabled={disabled}
    onClick={onClick}>
    {children}
  </Button>
)

export interface FaceOffGoldenReviewProps {
  data: FaceOffGoldenVersion
  // The signed-in camp. Album links open only for this camp's photos.
  clientId: number
  // Opens this case when it changes (a click in All cases).
  jumpTo: string | null
  // The version's details, behind the header's ⓘ, or null.
  summary?: FaceOffGoldenSummary | null
  // The removal queue and its Apply bar, when this version can be edited.
  queue?: RemovalQueue | null
  queueBar?: React.ReactNode
  // Whether the review covers the screen. Closed, Enter opens it.
  open: boolean
  onOpen: () => void
  // Esc: back to the All cases list.
  onAllCases: () => void
}

/**
 * One golden case at a time, as large as the screen allows: the album photo
 * with the matched face boxed, the camper's training photos beside it, the
 * baseline and latest scores, and A, W and U to save a verdict.
 */
export const FaceOffGoldenReview: React.FC<FaceOffGoldenReviewProps> = ({
  data,
  clientId,
  jumpTo,
  summary = null,
  queue = null,
  queueBar = null,
  open,
  onOpen,
  onAllCases,
}) => {
  const items = React.useMemo<ReviewItem[]>(
    () =>
      // A known miss is reported on its own each run and is not reviewed
      // here, as in the local Face Off app.
      data.photos.flatMap((photo) =>
        photo.cases
          .filter((c) => c.kind !== 'miss')
          .map((c) => ({photo, case: c})),
      ),
    [data],
  )
  const [cutoff, setCutoff] = React.useState(DEFAULT_CUTOFF)
  const [cutoffText, setCutoffText] = React.useState(String(DEFAULT_CUTOFF))
  const [includeSettled, setIncludeSettled] = React.useState(false)
  const [verdicts, setVerdicts] = React.useState<
    Record<string, FaceOffVerdictValue | null>
  >({})
  const [cur, setCur] = React.useState(-1)
  const [note, setNote] = React.useState('')
  const [message, setMessage] = React.useState('')
  const [enlarged, setEnlarged] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement | null>(null)

  const verdictOf = React.useCallback(
    (i: number): FaceOffVerdictValue | null => {
      const it = items[i]
      const id = caseId(it.photo.photoId, it.case.personId)
      return id in verdicts ? verdicts[id] : it.case.verdict?.verdict ?? null
    },
    [items, verdicts],
  )
  const order = React.useMemo(() => caseOrder(items, cutoff), [items, cutoff])
  const isRed = (i: number): boolean => {
    const s = items[i].case.score
    return s === null || s < cutoff
  }
  const eligible = React.useCallback(
    (i: number): boolean => includeSettled || !settled(verdictOf(i)),
    [includeSettled, verdictOf],
  )
  const step = React.useCallback(
    (i: number, dir: number): number => {
      for (let p = order.indexOf(i) + dir; p >= 0 && p < order.length; p += dir)
        if (eligible(order[p])) return order[p]
      return -1
    },
    [order, eligible],
  )

  // Start on the first case still to judge, again for each version.
  React.useEffect(() => {
    setVerdicts({})
    const start = caseOrder(items, DEFAULT_CUTOFF)
    const first = start.find((i) => !settled(items[i].case.verdict?.verdict))
    setCur(first ?? start[0] ?? -1)
  }, [items])

  React.useEffect(() => {
    if (!jumpTo) return
    const i = items.findIndex(
      (it) => caseId(it.photo.photoId, it.case.personId) === jumpTo,
    )
    if (i >= 0) {
      setCur(i)
      rootRef.current?.scrollIntoView?.({block: 'start'})
    }
  }, [jumpTo, items])

  const show = (i: number): void => {
    if (i < 0) return
    setCur(i)
    setNote('')
    setMessage('')
  }
  // Steps from the current case. A functional update, so keys held down or
  // pressed faster than React renders still each move one case.
  const move = React.useCallback(
    (dir: number): void => {
      setCur((i) => {
        const n = step(i, dir)
        return n < 0 ? i : n
      })
      setNote('')
      setMessage('')
    },
    [step],
  )

  // Warm the browser cache with the next three cases' photos.
  React.useEffect(() => {
    if (cur < 0) return
    let j = cur
    for (let n = 0; n < 3; n += 1) {
      j = step(j, 1)
      if (j < 0) return
      ;[items[j].photo.url, ...items[j].case.refUrls].forEach((src) => {
        new Image().src = src
      })
    }
  }, [cur, step, items])

  const decide = async (verdict: FaceOffVerdictValue): Promise<void> => {
    if (cur < 0) return
    const {photo, case: c} = items[cur]
    if (!data.latestRun) {
      setMessage(copy.review.noRunVerdict)
      return
    }
    if (!c.bbox) {
      setMessage(copy.review.noBoxVerdict)
      return
    }
    setMessage(copy.review.saving)
    const response = await api.faceoff.saveFaceOffVerdict(
      data.latestRun.runId,
      {
        face: {
          path: 'publish',
          photoId: photo.photoId,
          personId: c.personId,
          bbox: c.bbox,
        },
        verdict,
        note: note.trim(),
      },
    )
    if (response.result !== 'success') {
      setMessage(copy.review.notSaved)
      return
    }
    setVerdicts((v) => ({...v, [caseId(photo.photoId, c.personId)]: verdict}))
    // Step from the saved case; a settled case drops out of the walk.
    const next = step(cur, 1)
    if (next < 0) {
      setMessage(copy.review.end)
      return
    }
    show(next)
  }

  const keyRef = React.useRef<(event: KeyboardEvent) => void>(() => undefined)
  keyRef.current = (event: KeyboardEvent): void => {
    if (enlarged || cur < 0) return
    if (!open) {
      if (event.key === 'Enter' && !isTyping(event.target)) {
        event.preventDefault()
        onOpen()
      }
      return
    }
    const typing = isTyping(event.target)
    if (event.key === 'Escape') {
      onAllCases()
      return
    }
    if (typing && event.key !== 'Tab') return
    if (event.ctrlKey || event.metaKey || event.altKey) return
    const verdict = VERDICT_KEYS[event.key.toLowerCase()]
    if (verdict) {
      event.preventDefault()
      decide(verdict)
    } else if (
      (event.key === 'Tab' && !event.shiftKey) ||
      event.key === 'ArrowRight'
    ) {
      event.preventDefault()
      move(1)
    } else if (
      (event.key === 'Tab' && event.shiftKey) ||
      event.key === 'ArrowLeft'
    ) {
      event.preventDefault()
      move(-1)
    }
  }
  React.useEffect(() => {
    const handle = (event: KeyboardEvent): void => keyRef.current(event)
    window.addEventListener('keydown', handle)
    return (): void => window.removeEventListener('keydown', handle)
  }, [])
  const lightboxKey = React.useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault()
        move(event.key === 'ArrowRight' ? 1 : -1)
      }
    },
    [move],
  )

  if (items.length === 0 || cur < 0) {
    return <Text fontSize='sm'>{copy.review.empty}</Text>
  }

  const {photo, case: c} = items[cur]
  const red = isRed(cur)
  const tone = red ? BOX_COLORS.flip : BOX_COLORS.accepted
  const verdict = verdictOf(cur)
  const settledCount = items.filter((_, i) => settled(verdictOf(i))).length
  const redCount = order.filter(isRed).length
  const photoCount = data.photos.length
  // The cap counts every case, known misses too, though the walk skips them.
  const caseCount = data.photos.reduce((n, p) => n + p.cases.length, 0)
  const over = photoCount > MAX_GOLDEN_PHOTOS || caseCount > MAX_GOLDEN_CASES
  const boxes = c.bbox ? [{bbox: c.bbox, color: BOX_COLORS.matched}] : []
  const delta =
    c.score !== null && c.baselineScore !== null
      ? // Face Off signs a zero move here: (+0.0).
        formatDelta(c.score - c.baselineScore).replace(/^0/, '+0')
      : ''
  const canLink = photo.clientId === clientId && isLinkableId(photo.albumId)
  let latestText = formatScore(c.score)
  if (!data.latestRun) latestText = copy.review.noRun
  else if (c.score === null) latestText = copy.review.notScored
  const status = caseStatus(c, data.latestRun !== null)
  const prev = step(cur, -1)
  const next = step(cur, 1)

  if (!open) {
    return (
      <Button mb={6} onClick={onOpen} data-testid='face-off-review-open'>
        {copy.review.open}
      </Button>
    )
  }

  return (
    <Box
      ref={rootRef}
      position='fixed'
      inset={0}
      zIndex={1300}
      bg='var(--fo-bg)'
      overflowY='auto'
      px={4}
      py={3}
      data-testid='face-off-review'>
      <Flex gap={3} alignItems='center' flexWrap='wrap' mb={2}>
        <Button size='sm' variant='outline' onClick={onAllCases}>
          {copy.review.allCases}
        </Button>
        <Button
          size='sm'
          variant='ghost'
          aria-label={copy.review.prev}
          isDisabled={prev < 0}
          onClick={(): void => show(prev)}>
          ‹
        </Button>
        <Text fontWeight='bold' data-testid='face-off-review-position'>
          {fillCopy(copy.review.position, {
            pos: order.indexOf(cur) + 1,
            total: items.length,
            red: redCount,
            settled: settledCount,
          })}
        </Text>
        <Button
          size='sm'
          variant='ghost'
          aria-label={copy.review.next}
          isDisabled={next < 0}
          onClick={(): void => show(next)}>
          ›
        </Button>
        <Text fontWeight='bold' data-testid='face-off-review-camper'>
          {fillCopy(copy.review.camper, {personId: c.personId})}
        </Text>
        <Text
          fontSize='sm'
          color={status === 'regressed' ? 'var(--fo-red)' : 'var(--fo-muted)'}
          data-testid='face-off-review-status'>
          {copy.review.status[status]}
        </Text>
        {verdict && (
          <Text fontSize='sm' data-testid='face-off-review-saved'>
            {fillCopy(copy.review.saved, {
              verdict: copy.review.verdicts[verdict],
            })}
          </Text>
        )}
        <Flex
          as='label'
          alignItems='center'
          gap={2}
          fontSize='sm'
          color='var(--fo-muted)'>
          {copy.review.confidence}
          <input
            type='range'
            min={SLIDER_MIN}
            max={SLIDER_MAX}
            step={0.5}
            value={Math.min(SLIDER_MAX, Math.max(SLIDER_MIN, cutoff))}
            onChange={(e): void => {
              const v = parseFloat(e.target.value)
              setCutoff(v)
              setCutoffText(v.toFixed(1))
            }}
            data-testid='face-off-review-slider'
          />
        </Flex>
        <Input
          size='sm'
          w='80px'
          type='number'
          min={0}
          max={100}
          step={0.1}
          value={cutoffText}
          aria-label={copy.review.cutoffLabel}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setCutoffText(e.target.value)
            const v = parseFloat(e.target.value)
            if (!Number.isNaN(v) && v >= 0 && v <= 100) setCutoff(v)
          }}
          onBlur={(): void => setCutoffText(cutoff.toFixed(1))}
          data-testid='face-off-review-cutoff'
        />
        <Checkbox
          size='sm'
          isChecked={includeSettled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            setIncludeSettled(e.target.checked)
          }>
          {copy.review.includeSettled}
        </Checkbox>
        <Text
          fontSize='sm'
          color={over ? 'var(--fo-red)' : undefined}
          fontWeight={over ? 'bold' : undefined}
          data-testid='face-off-review-counts'>
          {fillCopy(copy.review.counts, {
            photos: photoCount,
            maxPhotos: MAX_GOLDEN_PHOTOS,
            cases: caseCount,
            maxCases: MAX_GOLDEN_CASES,
          })}
        </Text>
        {summary && (
          <Box as='details' fontSize='sm' data-testid='face-off-review-details'>
            <Box as='summary' cursor='pointer' color='var(--fo-link)'>
              {fillCopy(copy.golden.details.summary, {
                version: summary.version,
              })}
            </Box>
            <Box mt={2} p={2} borderWidth='1px' borderRadius='md'>
              <GoldenDetails summary={summary} />
            </Box>
          </Box>
        )}
      </Flex>
      <Flex
        gap={3}
        fontSize='sm'
        color='var(--fo-muted)'
        mb={3}
        flexWrap='wrap'>
        <Text data-testid='face-off-review-where'>
          {fillCopy(copy.review.where, {
            clientId: photo.clientId,
            albumId: photo.albumId,
            photoId: photo.photoId,
          })}
        </Text>
        {canLink && (
          <Link
            as={ReactRouterLink}
            to={buildAlbumPhotoUrl(photo.albumId, photo.photoId)}
            color='var(--fo-link)'
            textDecoration='underline'
            data-testid='face-off-review-album-link'>
            {copy.review.openAlbum}
          </Link>
        )}
        <Text>
          {fillCopy(copy.review.reason, {
            reason: photo.reason || copy.review.noReason,
          })}
          {c.kind ? fillCopy(copy.review.kind, {kind: c.kind}) : ''}
        </Text>
      </Flex>
      <Flex gap={4} justifyContent='center' alignItems='flex-start' mb={3}>
        <SideArrow
          label={copy.review.prev}
          disabled={prev < 0}
          onClick={(): void => show(prev)}>
          ‹
        </SideArrow>
        <Box textAlign='center' flex='3' minW={0}>
          <FaceOffPhoto
            src={photo.url}
            alt={fillCopy(copy.review.photoCaption, {photoId: photo.photoId})}
            boxes={boxes}
            maxH='calc(100vh - 230px)'
            frameColor={tone}
            onClick={(): void => setEnlarged(true)}
            testId='face-off-review-photo'
          />
          <Text fontSize='xs' color='var(--fo-muted)'>
            {fillCopy(copy.review.photoCaption, {photoId: photo.photoId})}
            {c.bbox ? '' : copy.review.noBox}
          </Text>
        </Box>
        <Flex direction='column' gap={2} flex='1' minW={0} alignItems='center'>
          {c.refUrls.length === 0 ? (
            <Text
              fontSize='sm'
              color='var(--fo-muted)'
              data-testid='face-off-review-no-training'>
              {copy.review.noTraining}
            </Text>
          ) : (
            c.refUrls.map((src, k) => (
              <Box key={src} textAlign='center'>
                <FaceOffPhoto
                  src={src}
                  alt={fillCopy(copy.review.trainingCaption, {
                    n: k + 1,
                    total: c.refUrls.length,
                  })}
                  boxes={[]}
                  maxH={`calc((100vh - 230px) / ${c.refUrls.length} - 24px)`}
                  testId='face-off-review-training'
                />
                <Text fontSize='xs' color='var(--fo-muted)'>
                  {fillCopy(copy.review.trainingCaption, {
                    n: k + 1,
                    total: c.refUrls.length,
                  })}
                </Text>
              </Box>
            ))
          )}
        </Flex>
        <SideArrow
          label={copy.review.next}
          disabled={next < 0}
          onClick={(): void => show(next)}>
          ›
        </SideArrow>
      </Flex>
      <Flex
        gap={7}
        justifyContent='center'
        flexWrap='wrap'
        fontSize='md'
        mb={3}
        data-testid='face-off-review-scores'>
        <Text>
          {copy.review.baseline}{' '}
          <b>
            {c.baselineScore === null
              ? copy.review.nonMatch
              : formatScore(c.baselineScore)}
          </b>
        </Text>
        <Text>
          {copy.review.latest}{' '}
          <Box as='b' color={red ? 'var(--fo-red)' : 'var(--fo-green)'}>
            {latestText}
          </Box>{' '}
          {delta && (
            <Box as='span' color={red ? 'var(--fo-red)' : 'var(--fo-green)'}>
              ({delta})
            </Box>
          )}{' '}
          {data.latestRun && (
            <Box as='span' color='var(--fo-muted)' fontSize='sm'>
              {fillCopy(copy.review.run, {
                runId: data.latestRun.runId,
                startedAt: data.latestRun.startedAt,
              })}
            </Box>
          )}
        </Text>
        <Text>
          {copy.review.cutoff} <b>{cutoff.toFixed(1)}</b>
        </Text>
      </Flex>
      <Flex gap={3} alignItems='center' flexWrap='wrap' justifyContent='center'>
        <Flex
          gap={2}
          alignItems='center'
          fontSize='sm'
          color='var(--fo-muted)'
          flexWrap='wrap'
          data-testid='face-off-review-keys'>
          {copy.review.keys.map(([key, label]) => (
            <Flex key={key} gap={1} alignItems='center'>
              <Key>{key}</Key>
              {label}
            </Flex>
          ))}
          <Text>{copy.review.enlargeHint}</Text>
        </Flex>
        {(['accept', 'reject', 'unsure'] as FaceOffVerdictValue[]).map((v) => (
          <Button
            key={v}
            size='sm'
            variant={verdict === v ? 'solid' : 'outline'}
            colorScheme={verdict === v ? 'blue' : undefined}
            onClick={(): void => {
              decide(v)
            }}
            data-testid={`face-off-review-${v}`}>
            {copy.review.verdicts[v]}
          </Button>
        ))}
        <Input
          size='sm'
          maxW='320px'
          placeholder={copy.review.notePlaceholder}
          value={note}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
            setNote(e.target.value)
          }
          data-testid='face-off-review-note'
        />
        <Text
          fontSize='sm'
          color='var(--fo-amber)'
          data-testid='face-off-review-message'>
          {message}
        </Text>
      </Flex>
      {queue && (
        <Flex
          gap={2}
          alignItems='center'
          flexWrap='wrap'
          justifyContent='center'
          mt={3}
          data-testid='face-off-review-edit'>
          <QueueCaseButton
            queue={queue}
            photoId={photo.photoId}
            personId={c.personId}
          />
          <QueuePhotoButton queue={queue} photo={photo} />
          {queueBar}
        </Flex>
      )}
      {enlarged && (
        <FaceOffLightbox
          src={photo.url}
          alt={fillCopy(copy.review.photoCaption, {photoId: photo.photoId})}
          boxes={boxes}
          onClose={(): void => setEnlarged(false)}
          onKey={lightboxKey}
        />
      )}
    </Box>
  )
}
